<script>
import InputTextEditor from '@/components(new)/UI/Forms/InputTextEditor/InputTextEditor.vue'
import AppDivider from '@/components/AppDivider.vue'
import AppLabel from '@/components/AppLabel.vue'
import AppModal from '@/components/AppModal.vue'
import AppNotificacao from '@/components/mixins/AppNotificacao.vue'
import estadosBr from '@/constants/estados'
import { opcoesEstadoCivil } from '@/libs/constants/proprietario'
import { mapActions } from 'vuex'
import InputDouble from '@/components(new)/UI/Forms/InputDouble/InputDouble.vue'
import { gerarTextoCertificado } from '@/constants/certificacao'
import messages from '@/utils/messages'
import { cloneDeep } from 'lodash'

export default {
  name: 'CertificacaoProcesso',

  mixins: [
    AppNotificacao
  ],

  components: {
    InputTextEditor,
    AppDivider,
    AppLabel,
    AppModal,
    InputDouble
  },

  props: {
    dadosCertificacao: {
      type: Object,
      required: true
    },
    etapaAtual: {
      type: Number,
      required: true
    },
    etapasCompletas: {
      type: Object,
      required: true
    },
    textoCertificado: {
      type: String,
      required: true
    },
    certificadoGerado: {
      type: [String, Object],
      default: null
    },
    loadingCertificado: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      estadosBrasileiros: [],
      municipios: [],
      municipiosImovel: [],
      opcoesEstadoCivil: [],
      dadosLocais: null,
      numeroCar: '',
      praticasRemovidas: [],
      modalPraticasRemovidas: false
    }
  },

  computed: {
    tituloEtapa () {
      const titulos = {
        1: 'Informar dados',
        2: 'Edição de certificado',
        3: 'Confirmar dados'
      }
      return titulos[this.etapaAtual] || ''
    },
    praticasParaExibir () {
      return this.dadosLocais?.praticas || []
    },
    isPessoaFisica () {
      return this.dadosLocais?.titular?.pessoa?.tipo === 'PF'
    },
    isPessoaJuridica () {
      return this.dadosLocais?.titular?.pessoa?.tipo === 'PJ'
    },
    isConjuge () {
      const result = this.isPessoaFisica && (
        this.dadosLocais?.titular?.pessoa?.pessoaFisica?.estadoCivil === 'CASADO' ||
        this.dadosLocais?.titular?.pessoa?.pessoaFisica?.estadoCivil === 'UNIAO_ESTAVEL'
      )
      return result
    }
  },

  watch: {
    dadosCertificacao: {
      immediate: true,
      handler (dadosAtuais) {
        if (dadosAtuais) {
          this.dadosLocais = cloneDeep(dadosAtuais)
          this.inicializarNumerosCar()
          this.inicializarCamposObrigatorios()
          this.gerarTextoAutomatico()
          this.converterPraticasParaCodigo()
        }
      }
    }
  },

  created () {
    this.inicializarDados()
    this.inicializarCamposObrigatorios()
    this.gerarTextoAutomatico()
  },

  mounted () {
    this.normalizarDatas()
    this.emitirDadosAlterados()
    if (this.dadosLocais?.titular?.pessoa?.pessoaFisica?.naturalidade?.codigoEstado) {
      this.carregarMunicipios(this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade.codigoEstado)
    }

    if (this.dadosLocais?.titular?.pessoa?.pessoaJuridica?.enderecoCorrespondencia?.municipio?.codigoEstado) {
      this.carregarMunicipios(this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.codigoEstado)
    }

    if (this.dadosLocais?.imovel?.endereco?.municipio?.codigoEstado) {
      this.carregarMunicipiosImovel(this.dadosLocais.imovel.endereco.municipio.codigoEstado)
    }
  },

  methods: {
    ...mapActions({
      getMunicipiosByUf: 'municipios/findMunicipiosByUf',
      enderecoByCep: 'endereco/findEnderecoByCep'
    }),

    formatarDataArrayParaString (dataArray) {
      if (dataArray && Array.isArray(dataArray)) {
        const [ano, mes, dia] = dataArray
        return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
      }
      return dataArray
    },

    obterDataAtualFormatada () {
      const hoje = new Date()
      return hoje.toISOString().split('T')[0]
    },
    normalizarDataEmissao () {
      if (!this.dadosLocais?.caracteristicas) {
        this.dadosLocais.caracteristicas = {}
      }

      let dataEmissao = this.dadosLocais.caracteristicas.dataEmissao

      dataEmissao = this.formatarDataArrayParaString(dataEmissao)

      if (!dataEmissao || dataEmissao === '' || dataEmissao === null || dataEmissao === undefined) {
        dataEmissao = this.obterDataAtualFormatada()
      }

      this.dadosLocais.caracteristicas.dataEmissao = dataEmissao
    },

    normalizarPrazosPraticas () {
      if (this.dadosLocais?.praticas) {
        this.dadosLocais.praticas.forEach(pratica => {
          if (pratica.prazo) {
            pratica.prazo = this.formatarDataArrayParaString(pratica.prazo)
          } else {
            pratica.prazo = ''
          }

          if (this.isPraticaMRPA(pratica)) {
            pratica.area = null
          } else if (!pratica.area) {
            pratica.area = null
          }
        })
      }
    },

    normalizarDatas () {
      this.normalizarDataEmissao()
      this.normalizarPrazosPraticas()
    },

    inicializarDados () {
      this.estadosBrasileiros = estadosBr.map(estado => ({
        codigo: estado.codigo,
        nome: estado.nome
      }))
      this.opcoesEstadoCivil = opcoesEstadoCivil.map(estado => ({
        codigo: estado.id,
        nome: estado.nome
      }))
    },

    async carregarMunicipios (uf) {
      if (uf) {
        try {
          const municipios = await this.getMunicipiosByUf(uf)
          this.municipios = municipios.map(municipio => ({
            id: municipio.id,
            nome: municipio.nome
          }))
        } catch (error) {
          console.error('Erro ao carregar municípios:', error)
          this.municipios = []
        }
      } else {
        this.municipios = []
      }
    },

    async carregarMunicipiosImovel (uf) {
      if (uf) {
        try {
          const municipios = await this.getMunicipiosByUf(uf)
          this.municipiosImovel = municipios.map(municipio => ({
            id: municipio.id,
            nome: municipio.nome
          }))
        } catch (error) {
          console.error('Erro ao carregar municípios:', error)
          this.municipiosImovel = []
        }
      } else {
        this.municipiosImovel = []
      }
    },

    async onUfChange (uf, tipo) {
      if (tipo === 'pessoaFisica') {
        if (!this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade) {
          this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade = {}
        }
        this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade.codigoEstado = uf
        this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade.id = null
        this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade.nome = null
      } else if (tipo === 'pessoaJuridica') {
        if (!this.dadosLocais.titular.pessoa.enderecoCorrespondencia) {
          this.dadosLocais.titular.pessoa.enderecoCorrespondencia = {}
        }
        if (!this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio) {
          this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio = {}
        }
        this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.codigoEstado = uf
        this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.id = null
        this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.nome = null
      }
      await this.carregarMunicipios(uf)
      this.emitirDadosAlterados()
    },

    async onUfImovelChange (uf) {
      if (!this.dadosLocais.imovel.endereco.municipio) {
        this.dadosLocais.imovel.endereco.municipio = {}
      }
      this.dadosLocais.imovel.endereco.municipio.codigoEstado = uf
      this.dadosLocais.imovel.endereco.municipio.id = null
      this.dadosLocais.imovel.endereco.municipio.nome = null
      await this.carregarMunicipiosImovel(uf)
      this.emitirDadosAlterados()
    },

    onMunicipioImovelChange (municipioId) {
      if (municipioId) {
        const municipio = this.municipiosImovel.find(m => m.id === municipioId)
        if (municipio) {
          this.dadosLocais.imovel.endereco.municipio = {
            id: municipio.id,
            nome: municipio.nome,
            codigoEstado: this.dadosLocais.imovel.endereco.municipio.codigoEstado
          }
        }
      } else {
        this.dadosLocais.imovel.endereco.municipio = {
          id: null,
          nome: null,
          codigoEstado: this.dadosLocais.imovel.endereco.municipio.codigoEstado
        }
      }
      this.emitirDadosAlterados()
    },

    onMunicipioChange (municipioId) {
      if (municipioId) {
        const municipio = this.municipios.find(m => m.id === municipioId)
        if (municipio) {
          this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade = {
            id: municipio.id,
            nome: municipio.nome,
            codigoEstado: this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade.codigoEstado
          }
        }
      } else {
        this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade = {
          id: null,
          nome: null,
          codigoEstado: this.dadosLocais.titular.pessoa.pessoaFisica.naturalidade.codigoEstado
        }
      }
      this.emitirDadosAlterados()
    },

    onMunicipioJuridicoChange (municipioId) {
      if (municipioId) {
        const municipio = this.municipios.find(m => m.id === municipioId)
        if (municipio) {
          this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio = {
            id: municipio.id,
            nome: municipio.nome,
            codigoEstado: this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.codigoEstado
          }
        }
      } else {
        this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio = {
          id: null,
          nome: null,
          codigoEstado: this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.codigoEstado
        }
      }
      this.emitirDadosAlterados()
    },

    async sincronizarMunicipioJuridico () {
      const uf = this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.codigoEstado
      if (uf) {
        await this.carregarMunicipios(uf)
        const municipioId = this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.id
        const municipioEncontrado = this.municipios.find(m => m.id === municipioId)
        if (municipioEncontrado) {
          this.dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.nome = municipioEncontrado.nome
        }
      }
    },

    async onEstadoCivilChange (newVal) {
      this.dadosLocais.titular.pessoa.pessoaFisica.estadoCivil = newVal
      const changeConjuge = newVal === 'CASADO' || newVal === 'UNIAO_ESTAVEL'

      if (changeConjuge) {
        if (!this.dadosLocais.titular.conjuge) {
          this.dadosLocais.titular.conjuge = {
            pessoaFisica: {
              nome: '',
              cpf: ''
            }
          }
        } else if (!this.dadosLocais.titular.conjuge.pessoaFisica) {
          this.dadosLocais.titular.conjuge.pessoaFisica = {
            nome: '',
            cpf: ''
          }
        }
      } else {
        if (this.dadosLocais.titular.conjuge) {
          this.dadosLocais.titular.conjuge.pessoaFisica.nome = ''
          this.dadosLocais.titular.conjuge.pessoaFisica.cpf = ''
        }
      }

      this.emitirDadosAlterados()
    },

    async buscarInfoByCep () {
      if (!this.dadosLocais.imovel.endereco.cep || this.dadosLocais.imovel.endereco.cep.length !== 9) {
        return
      }

      try {
        const data = await this.enderecoByCep(this.dadosLocais.imovel.endereco.cep)

        if (data.erro) {
          console.warn('CEP não encontrado')
          return
        }

        this.dadosLocais.imovel.endereco.logradouro = data.logradouro || ''
        this.dadosLocais.imovel.endereco.localidade = data.localidade || ''

        if (data.uf) {
          if (!this.dadosLocais.imovel.endereco.municipio) {
            this.dadosLocais.imovel.endereco.municipio = {}
          }
          this.dadosLocais.imovel.endereco.municipio.codigoEstado = data.uf
          await this.carregarMunicipiosImovel(data.uf)

          if (data.ibge) {
            const municipioEncontrado = this.municipiosImovel.find(m => m.id === Number(data.ibge))
            if (municipioEncontrado) {
              this.dadosLocais.imovel.endereco.municipio = {
                id: municipioEncontrado.id,
                nome: municipioEncontrado.nome,
                codigoEstado: data.uf
              }
            }
          }
        }

        this.emitirDadosAlterados()
      } catch (error) {
        console.error('Erro ao buscar CEP:', error)
      }
    },

    onSemNumeroCheck (checked) {
      if (checked) {
        this.dadosLocais.imovel.endereco.numero = null
      }
      this.emitirDadosAlterados()
    },

    removerPratica (index) {
      this.confirmBox(
        messages.PRATICA_REMOVE_CONFIRM,
        messages.TITULO_WARNING,
        () => {
          this.confirmarRemocaoPratica(index)
        },
        () => {
        }
      )
    },

    confirmarRemocaoPratica (index) {
      const praticaRemovida = this.dadosLocais.praticas[index]
      this.praticasRemovidas.push(praticaRemovida)
      const novasPraticas = [...this.dadosLocais.praticas]
      novasPraticas.splice(index, 1)
      this.dadosLocais.praticas = novasPraticas

      this.$emit('dadosAlterados', this.dadosLocais)
    },

    readicionarPratica (index) {
      const pratica = this.praticasRemovidas[index]
      this.praticasRemovidas.splice(index, 1)
      this.dadosLocais.praticas.push(pratica)

      this.emitirDadosAlterados()
      if (this.praticasRemovidas.length === 0) {
        this.modalPraticasRemovidas = false
      }
    },

    gerarTextoAutomatico () {
      if (this.dadosLocais) {
        const textoGerado = gerarTextoCertificado(this.dadosLocais)
        this.$emit('textoAlterado', textoGerado)
      }
    },

    emitirDadosAlterados () {
      this.normalizarDatas()
      this.$emit('dadosAlterados', this.dadosLocais)
      this.gerarTextoAutomatico()
    },

    emitirTextoAlterado () {
      this.$emit('textoAlterado', this.textoCertificado)
    },

    addCarNum (carNumber) {
      if (carNumber && carNumber.length >= 50) {
        if (!this.dadosLocais.imovel.numerosRecibosCar.includes(carNumber)) {
          this.dadosLocais.imovel.numerosRecibosCar.push(carNumber)
        }
        this.numeroCar = ''
        this.emitirDadosAlterados()
      }
    },

    removeCarNum (carItem) {
      const index = this.dadosLocais.imovel.numerosRecibosCar.indexOf(carItem)
      if (index > -1) {
        this.dadosLocais.imovel.numerosRecibosCar.splice(index, 1)
        this.emitirDadosAlterados()
      }
    },

    inicializarNumerosCar () {
      if (!this.dadosLocais?.imovel?.numerosRecibosCar) {
        this.dadosLocais.imovel.numerosRecibosCar = []
        return
      }

      this.dadosLocais.imovel.numerosRecibosCar = this.dadosLocais.imovel.numerosRecibosCar.map(item => {
        if (typeof item === 'string') {
          return item
        } else if (item && typeof item === 'object' && item.numero) {
          return item.numero
        } else {
          return String(item)
        }
      }).filter(item => item && item.trim() !== '')
    },

    converterPraticasParaCodigo () {
      if (this.dadosLocais?.praticas) {
        this.dadosLocais.praticas.forEach((pratica, index) => {
          if (pratica.pratica && typeof pratica.pratica === 'object' && pratica.pratica.codigo) {
            const codigoOriginal = pratica.pratica.codigo
            pratica.pratica = codigoOriginal
          }
        })
      }
    },

    inicializarCamposObrigatorios () {
      this.normalizarDatas()
      if (this.isConjuge && this.dadosLocais?.titular) {
        if (!this.dadosLocais.titular.conjuge) {
          this.dadosLocais.titular.conjuge = {
            pessoaFisica: {
              nome: '',
              cpf: ''
            }
          }
        } else if (!this.dadosLocais.titular.conjuge.pessoaFisica) {
          this.dadosLocais.titular.conjuge.pessoaFisica = {
            nome: '',
            cpf: ''
          }
        }
      }
      if (this.dadosLocais?.titular?.pessoa?.enderecoCorrespondencia?.municipio?.codigoEstado) {
        this.sincronizarMunicipioJuridico()
      }
    },

    limparCamposNull () {
      if (this.dadosLocais?.praticas) {
        this.dadosLocais.praticas.forEach(pratica => {
          if (pratica.prazo === null) {
            pratica.prazo = ''
          }
        })
      }

      this.normalizarDataEmissao()
      if (this.dadosLocais?.titular?.pessoa?.pessoaFisica?.dataNascimento === null) {
        this.dadosLocais.titular.pessoa.pessoaFisica.dataNascimento = ''
      }
    },

    addPratica () {
      if (this.praticasRemovidas.length > 0) {
        this.modalPraticasRemovidas = true
        return
      }
      this.dadosLocais.praticas.push({
        pratica: '',
        area: null,
        prazo: ''
      })
      this.emitirDadosAlterados()
    },

    getNomeComSigla (pratica) {
      if (pratica.pratica && typeof pratica.pratica === 'object' && pratica.pratica.nomeComSigla) {
        return pratica.pratica.nomeComSigla
      }
      if (typeof pratica.pratica === 'string') {
        return pratica.pratica
      }
      return 'Prática não identificada'
    },
    isPraticaMRPA (pratica) {
      const isPratica = (pratica.pratica === 'MRPA') ||
                       (pratica.pratica && typeof pratica.pratica === 'object' && pratica.pratica.codigo === 'MRPA')
      return isPratica
    },
    isDataFutura (data) {
      if (!data) return true
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)
      const dataSelecionada = new Date(data)
      return dataSelecionada > hoje
    }
  }
}
</script>

<template>
  <validation-observer ref="observer" tag="form">
    <div class="certificacao-processo">
      <div class="etapas-progresso mt-3">
        <div class="steps-container">
          <div
            v-for="etapa in 3"
            :key="etapa"
            :class="['step', { 'active': etapa === etapaAtual, 'completed': etapasCompletas[etapa] }]"
          >
            <div class="step-number">{{ etapa }}</div>
            <div class="step-label">
              {{ etapa === 1 ? 'Informar dados' : etapa === 2 ? 'Edição certificado' : 'Confirmar dados' }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="etapaAtual === 1">
        <app-divider label="Características do certificado" />
        <el-row :gutter="20">
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Número do certificado">
              <app-label label="Nº do certificado" :required="true" />
              <el-input
                v-model="dadosLocais.caracteristicas.numeroCertificado"
                @input="emitirDadosAlterados"
                :class="{'input erro': errors.length}"
                 :disabled="true"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Data de emissão">
              <app-label label="Data de emissão" :required="true" />
              <el-date-picker
                v-model="dadosLocais.caracteristicas.dataEmissao"
                @input="emitirDadosAlterados"
                type="date"
                placeholder="DD/MM/AAAA"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                style="width: 100%"
                :class="{'input erro': errors.length}"
                 :disabled="true"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Processo administrativo">
              <app-label label="Processo administrativo" :required="true" />
              <el-input
                v-model="dadosLocais.caracteristicas.numeroProcesso"
                @input="emitirDadosAlterados"
                :class="{'input erro': errors.length}"
                 :disabled="true"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="16">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Órgão certificador">
              <app-label label="Órgão certificador" :required="true" />
              <el-input
                v-model="dadosLocais.caracteristicas.orgaoCertificador"
                @input="emitirDadosAlterados"
                :class="{'input erro': errors.length}"
                :disabled="true"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
        </el-row>

        <app-divider label="Titular do imóvel" />
        <div v-if="isPessoaFisica">
          <el-row :gutter="12">
          <el-col :span="12">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Nome do titular">
              <app-label label="Nome do titular" :required="true" />
              <el-input
                v-model="dadosLocais.titular.pessoa.pessoaFisica.nome"
                @input="emitirDadosAlterados"
                :class="{'input erro': errors.length}"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="6">
            <validation-provider rules="required|cpf" v-slot="{ errors }"
            mode="lazy" name="certificacao/CPF">
              <app-label label="CPF" :required="true" />
              <el-input
                v-model="dadosLocais.titular.pessoa.pessoaFisica.cpf"
                @input="emitirDadosAlterados"
                v-mask="['###.###.###-##']"
                placeholder="000.000.000-00"
                clearable
                :class="{'input erro': errors.length}"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="6">
            <validation-provider rules="required" v-slot="{ errors }"
            mode="lazy" name="certificacao/Data de nascimento">
              <app-label label="Data de nascimento" :required="true" />
              <el-date-picker
                v-model="dadosLocais.titular.pessoa.pessoaFisica.dataNascimento"
                @input="emitirDadosAlterados"
                type="date"
                placeholder="DD/MM/AAAA"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                style="width: 100%"
                :class="{'input erro': errors.length}"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }"
            name="certificacao/UF de naturalidade">
              <app-label label="UF de naturalidade" :required="true" />
              <el-select
                v-model="dadosLocais.titular.pessoa.pessoaFisica.naturalidade.codigoEstado"
                @change="(uf) => onUfChange(
                  uf, 'pessoaFisica', 'naturalidade', 'codigoEstado')"
                placeholder="Selecione"
                clearable
                filterable
                :class="{'input erro': errors.length}"
                style="width: 100%"
              >
                <el-option
                  v-for="estado in estadosBrasileiros"
                  :key="estado.codigo"
                  :label="estado.nome"
                  :value="estado.codigo"
                />
              </el-select>
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Município de naturalidade">
              <app-label label="Município de naturalidade" :required="true" />
              <el-select
                v-model="dadosLocais.titular.pessoa.pessoaFisica.naturalidade.id"
                @change="onMunicipioChange"
                placeholder="Selecione"
                clearable
                filterable
                :disabled="!dadosLocais.titular.pessoa.pessoaFisica.naturalidade.codigoEstado"
                :class="{'input erro': errors.length}"
                style="width: 100%"
              >
                  <el-option
                    v-for="municipio in municipios"
                    :key="municipio.id"
                    :label="municipio.nome"
                    :value="municipio.id"
                  />
              </el-select>
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Estado civil">
              <app-label label="Estado civil" :required="true" />
              <el-select
                v-model="dadosLocais.titular.pessoa.pessoaFisica.estadoCivil"
                @change="onEstadoCivilChange"
                placeholder="Selecione"
                clearable
                filterable
                :class="{'input erro': errors.length}"
                style="width: 100%"
              >
                <el-option
                  v-for="estado in opcoesEstadoCivil"
                  :key="estado.codigo"
                  :label="estado.nome"
                  :value="estado.codigo"
                />
              </el-select>
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
        </el-row>
      </div>
        <div v-if="isPessoaJuridica">
          <el-row :gutter="30">
            <el-col :span="12">
              <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Razão social">
                <app-label label="Razão social" :required="true" />
                <el-input
                  v-model="dadosLocais.titular.pessoa.pessoaJuridica.razaoSocial"
                  @input="emitirDadosAlterados"
                  placeholder="Digite a razão social"
                  :class="{'input erro': errors.length}"
                />
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
            <el-col :span="6">
              <validation-provider rules="required|cpf_cnpj" v-slot="{ errors }" mode="lazy" name="certificacao/CNPJ">
                <app-label label="CNPJ" :required="true" />
                <el-input
                  v-model="dadosLocais.titular.pessoa.pessoaJuridica.cnpj"
                  @input="emitirDadosAlterados"
                  v-mask="['##.###.###/####-##']"
                  placeholder="00.000.000/0000-00"
                  clearable
                  :class="{'input erro': errors.length}"
                />
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
              <el-col :span="6">
                <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Inscrição estadual">
                  <app-label label="Inscrição estadual" :required="true" />
                  <el-input
                    v-model="dadosLocais.titular.pessoa.pessoaJuridica.inscricaoEstadual"
                    @input="emitirDadosAlterados"
                    placeholder="Digite a inscrição estadual"
                    :class="{'input erro': errors.length}"
                  />
                  <span class="help erro">{{ errors[0] }}</span>
                </validation-provider>
            </el-col>
          </el-row>
          <el-row :gutter="30">
            <el-col :span="12">
              <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Nome fantasia">
                <app-label label="Nome fantasia" :required="true" />
                <el-input
                  v-model="dadosLocais.titular.pessoa.pessoaJuridica.nomeFantasia"
                  @input="emitirDadosAlterados"
                  placeholder="Digite o nome fantasia"
                  :class="{'input erro': errors.length}"
                />
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
            <el-col :span="6">
              <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/UF">
                <app-label label="UF" :required="true" />
                <el-select
                  v-model="dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.codigoEstado"
                  @change="(uf) => onUfChange(uf, 'pessoaJuridica')"
                  placeholder="Selecione"
                  clearable
                  filterable
                  :class="{'input erro': errors.length}"
                  style="width: 100%"
                >
                  <el-option
                    v-for="estado in estadosBrasileiros"
                    :key="estado.codigo"
                    :label="estado.nome"
                    :value="estado.codigo"
                  />
                </el-select>
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
            <el-col :span="6">
              <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Município">
                <app-label label="Município" :required="true" />
                <el-select
                  v-model="dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.id"
                  @change="onMunicipioJuridicoChange"
                  placeholder="Selecione"
                  clearable
                  filterable
                  :disabled="!dadosLocais.titular.pessoa.enderecoCorrespondencia.municipio.codigoEstado"
                  :class="{'input erro': errors.length}"
                  style="width: 100%"
                >
                  <el-option
                    v-for="municipio in municipios"
                    :key="municipio.id"
                    :label="municipio.nome"
                    :value="municipio.id"
                  />
                </el-select>
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
          </el-row>
        </div>
        <div v-if="isConjuge">
          <app-divider label="Dados do cônjuge" />
          <el-row :gutter="20">
            <el-col :span="12">
              <validation-provider :rules="isConjuge ? 'required' : ''" v-slot="{ errors }" mode="lazy" name="certificacao/Nome do cônjuge">
                <app-label label="Nome do cônjuge" :required="true" />
                <el-input
                  v-model="dadosLocais.titular.conjuge.pessoaFisica.nome"
                  @input="emitirDadosAlterados"
                  :class="{'input erro': errors.length}"
                />
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
            <el-col :span="12">
              <validation-provider :rules="isConjuge ? 'required|cpf' : ''" v-slot="{ errors }" mode="lazy" name="certificacao/CPF do cônjuge">
                <app-label label="CPF do cônjuge" :required="true" />
                <el-input
                  v-model="dadosLocais.titular.conjuge.pessoaFisica.cpf"
                  @input="emitirDadosAlterados"
                  v-mask="['###.###.###-##']"
                  placeholder="000.000.000-00"
                  clearable
                  :class="{'input erro': errors.length}"
                />
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
          </el-row>
        </div>

        <app-divider label="Dados do imóvel" />
        <el-row :gutter="20">
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Nome do imóvel">
              <app-label label="Nome do imóvel" :required="true" />
              <el-input
                v-model="dadosLocais.imovel.nome"
                @input="emitirDadosAlterados"
                :class="{'input erro': errors.length}"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="8">
            <validation-provider v-slot="{ errors }" mode="lazy" name="certificacao/Número do CAR">
              <app-label label="Número do CAR" :required="false" />
              <el-input
                v-model="numeroCar"
                v-mask="'AA-XXXXXXX-XXXX.XXXX.XXXX.XXXX.XXXX.XXXX.XXXX.XXXX'"
                placeholder="UF-0000000-0000.0000.0000.0000.0000.0000.0000.0000"
                clearable
                :class="{'input erro': errors.length}"
              >
                <template #append>
                  <el-button
                    class="style"
                    icon="el-icon-plus"
                    @click="addCarNum(numeroCar)"
                    :disabled="!numeroCar || numeroCar.length < 50"
                  />
                </template>
              </el-input>

              <div v-if="dadosLocais.imovel.numerosRecibosCar.length > 0" class="tag-app-input marginTop">
                <el-tag
                  v-for="(numCar, index) in dadosLocais.imovel.numerosRecibosCar"
                  :key="index"
                  closable
                  type="info"
                  size="medium"
                  @close="removeCarNum(numCar)"
                >
                  {{ numCar }}
                </el-tag>
              </div>

              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="8">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/CEP">
              <app-label label="CEP" :required="true" />
              <el-input
                v-model="dadosLocais.imovel.endereco.cep"
                @input="emitirDadosAlterados"
                @change="buscarInfoByCep"
                placeholder="99999-999"
                v-mask="['#####-###']"
                clearable
                :class="{'input erro': errors.length}"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/UF do imóvel">
              <app-label label="UF" :required="true" />
              <el-select
                v-model="dadosLocais.imovel.endereco.municipio.codigoEstado"
                @change="onUfImovelChange"
                placeholder="Selecione"
                clearable
                filterable
                :class="{'input erro': errors.length}"
                style="width: 100%"
              >
                <el-option
                  v-for="estado in estadosBrasileiros"
                  :key="estado.codigo"
                  :label="estado.nome"
                  :value="estado.codigo"
                />
              </el-select>
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="6">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Município do imóvel">
              <app-label label="Município" :required="true" />
              <el-select
                v-model="dadosLocais.imovel.endereco.municipio.id"
                @change="onMunicipioImovelChange"
                placeholder="Selecione"
                clearable
                filterable
                :disabled="!dadosLocais.imovel.endereco.municipio.codigoEstado"
                :class="{'input erro': errors.length}"
                style="width: 100%"
              >
                <el-option
                  v-for="municipio in municipiosImovel"
                  :key="municipio.id"
                  :label="municipio.nome"
                  :value="municipio.id"
                />
              </el-select>
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
          <el-col :span="6">
            <app-label label="Logradouro" :required="false" />
            <el-input
              v-model="dadosLocais.imovel.endereco.logradouro"
              @input="emitirDadosAlterados"
            />
          </el-col>
          <el-col :span="6">
            <validation-provider rules="required" v-slot="{ errors }" mode="lazy" name="certificacao/Localidade/Comunidade">
              <app-label label="Localidade/Comunidade" :required="true" />
              <el-input
                v-model="dadosLocais.imovel.endereco.comunidadeLocalidade"
                @input="emitirDadosAlterados"
                :class="{'input erro': errors.length}"
              />
              <span class="help erro">{{ errors[0] }}</span>
            </validation-provider>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="4">
            <app-label label="Número" :required="false" />
            <el-input
              v-model="dadosLocais.imovel.endereco.numero"
              @input="emitirDadosAlterados"
              :disabled="dadosLocais.imovel.endereco.semNumero"
              clearable
            />
            <el-checkbox
              v-model="dadosLocais.imovel.endereco.semNumero"
              @change="onSemNumeroCheck"
              style="margin-top: 8px;"
            >Sem número</el-checkbox>
          </el-col>
        </el-row>

        <app-divider label="Práticas Sustentáveis" />
        <el-row :gutter="20" style="margin-top: 30px; margin-bottom: 20px;">
          <el-col :span="24" style="text-align: right;">
            <el-button
              type="primary"
              icon="el-icon-plus"
              @click="addPratica"
              size="medium"
              :disabled="praticasRemovidas.length === 0"
            >
              {{ 'Adicionar Prática' }}
            </el-button>
          </el-col>
        </el-row>

        <div v-for="(pratica, index) in praticasParaExibir" :key="index">
          <el-row :gutter="20">
            <el-col :span="8">
              <validation-provider rules="required" v-slot="{ errors }" mode="lazy" :name="`certificacao/Prática ${index + 1} - Nome`">
                <app-label label="Nome da prática" :required="true" />
                <el-input
                  :value="getNomeComSigla(pratica)"
                  placeholder="Digite o código da prática (ex: SAF, TI)"
                  :class="{'input erro': errors.length}"
                  readonly
                  :disabled="true"
                />
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
            <el-col :span="6">
              <validation-provider :rules="isPraticaMRPA(pratica) ? '' : 'required|area'" v-slot="{ errors }" mode="lazy" :name="`certificacao/Prática ${index + 1} - Área`">
                <app-label label="Área da prática (ha)" :required="!isPraticaMRPA(pratica)" />
                <el-input
                  v-if="isPraticaMRPA(pratica)"
                  value="-"
                  disabled
                  class="area-mrpa-disabled"
                />
                <InputDouble
                  v-else
                  :class="{ erro: errors.length, input: 'input' }"
                  name="Área da prática (ha)"
                  :precision="2"
                  :showAppend="true"
                  v-model="pratica.area"
                />
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
            <el-col :span="8">
              <validation-provider rules="required" v-slot="{ errors }" mode="lazy" :name="`certificacao/Prática ${index + 1} - Prazo`">
                <app-label label="Prazo da certificação" :required="true" />
                <div style="display: flex; gap: 10px; align-items: flex-end;">
                  <el-date-picker
                    v-model="pratica.prazo"
                    @input="emitirDadosAlterados"
                    placeholder="DD/MM/AAAA"
                    format="dd/MM/yyyy"
                    value-format="yyyy-MM-dd"
                    style="width: 100%"
                    clearable
                    :class="{'input erro': errors.length}"
                    :picker-options="{
                      disabledDate: (time) => !isDataFutura(time)
                    }"
                  />
                  <el-button
                    type="text"
                    size="medium"
                    @click="removerPratica(index)"
                    :disabled="praticasParaExibir.length <= 1"
                    :style="praticasParaExibir.length <= 1 ? 'color: #C0C4CC; height: 40px; border: none; background: transparent; white-space: nowrap; font-size: 16px; cursor: not-allowed;' : 'color: #F56C6C; height: 40px; border: none; background: transparent; white-space: nowrap; font-size: 16px;'"
                  >
                    <i class="el-icon-close" style="font-size: 18px; margin-right: 5px;"></i> Remover
                  </el-button>
                </div>
                <span class="help erro">{{ errors[0] }}</span>
              </validation-provider>
            </el-col>
          </el-row>
        </div>
      </div>
      <div v-if="etapaAtual === 2" class="etapa-2">
        <app-divider label="Características do certificado" />
        <el-row :gutter="20">
          <el-col :span="24">
            <input-text-editor
              label="Informações do certificado"
              v-model="textoCertificado"
              @input="emitirTextoAlterado"
              placeholder="Digite o texto do certificado..."
              validationRules="required"
            />
          </el-col>
        </el-row>
      </div>

      <div v-if="etapaAtual === 3">
        <app-divider label="Confirmar dados" />

        <div v-if="loadingCertificado" class="loading-container">
          <i class="el-icon-loading"></i>
          <p>Gerando certificado...</p>
        </div>

        <div v-else-if="certificadoGerado" class="certificado-container">
          <h4>Certificado Gerado</h4>
          <div class="certificado-viewer">
            <iframe
              :src="certificadoGerado"
              width="100%"
              height="600px"
              frameborder="0"
              title="Visualização do Certificado"
            ></iframe>
          </div>
        </div>

        <div v-else class="gerar-certificado">
          <p>Clique em "Finalizar" para gerar o certificado com base nos dados informados.</p>
        </div>
      </div>
      <el-dialog
        title="Selecione qual prática deseja inserir"
        :visible.sync="modalPraticasRemovidas"
        width="500px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
        custom-class="modal-praticas-removidas"
      >
        <div v-if="praticasRemovidas.length === 0" class="no-practices">
          <p>Não há práticas removidas disponíveis para readicionar.</p>
        </div>
        <div v-else class="practices-list">
          <div
            v-for="(pratica, index) in praticasRemovidas"
            :key="index"
            class="practice-item"
            @click="readicionarPratica(index)"
          >
            <div class="practice-info">
              <span class="practice-name">{{ getNomeComSigla(pratica) }}</span>
            </div>
            <i class="el-icon-plus" style="color: #009F61;"></i>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="modalPraticasRemovidas = false">Fechar</el-button>
        </span>
      </el-dialog>
    </div>
  </validation-observer>
</template>

<style lang="scss" scoped>
@import './styles.scss';
</style>



// WEBPACK FOOTER //
// src/App/(authenticated)/modulos/analise/certificacao/emissao-certificado/components/CertificacaoProcesso.vue